import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Printer } from 'lucide-react';

const EFFECTIVE_DATE = 'February 12, 2026';
const LAST_UPDATED = 'February 12, 2026';

const SECTIONS = [
  { id: 'intro', heading: 'Overview', body: [
    "Brotherhood+ is a wellness, growth, and support platform for men — rooted in culture — and the people who support them. This Privacy Policy explains what we collect, why we collect it, how we use and protect it, and the choices you have.",
    "This policy applies to brotherhoodplus.com, our web and mobile apps, and any related services (together, the 'Service').",
  ]},
  { id: 'info-collected', heading: '1. Information we collect', body: [
    "We collect only what makes the Service useful.",
    { subheading: 'Account and profile data', text: "Name, email address, hashed password, age (birth year), role (member or supporter), goals, support style, faith preference, and any relationship-to-a-member context you choose to share during onboarding." },
    { subheading: 'AI Coach and Journal data', text: "The prompts you send to the AI Coach and the entries you save in the Journal are stored so we can display your history to you and so the coach can maintain conversational context. AI responses are generated by industry-leading language models with additional safety layers." },
    { subheading: 'Wellness content interactions', text: "Assessments you complete, lessons you finish, check-ins you record, growth plans you enroll in, and Lounge game results you save." },
    { subheading: 'Analytics and device information', text: "Anonymous page views, event names, approximate device type, browser, operating system, timezone, and coarse IP-derived geolocation (country/region). We do NOT collect precise GPS location. We do NOT track you across other websites." },
    { subheading: 'Cookies and authentication', text: "We use secure, httpOnly session cookies (bp_access, bp_refresh) to keep you signed in, a JavaScript-readable CSRF token (bp_csrf), and — if you enable it — a 30-day trusted-device cookie (bp_trusted). We do not use advertising cookies." },
  ]},
  { id: 'how-used', heading: '2. How we use your information', body: [
    "We use your information to:",
    { list: [
      "Operate and improve the Service (Coach, Journal, Learn, Progress, Lounge, Directory, Community).",
      "Personalize the experience — e.g. show the right growth plan or check-in prompt.",
      "Send transactional emails (account verification, password reset, security alerts). We do NOT send marketing emails without your opt-in.",
      "Detect and prevent abuse, fraud, and account takeover.",
      "Comply with legal obligations and enforce our Terms of Service.",
    ]},
    "We do NOT sell your personal information. We do NOT share Journal entries, coach conversations, or assessment results with advertisers or data brokers.",
  ]},
  { id: 'service-providers', heading: '3. Service providers', body: [
    "We share limited information with the following processors, each under a data-processing agreement:",
    { list: [
      "Anthropic and other language-model providers — to generate AI Coach and Journal reflections. Prompts are transmitted; providers do not retain them for training on our account.",
      "MongoDB Atlas / cloud infrastructure — hosting and storage.",
    ]},
    "We do not authorize any of these providers to use your data for their own advertising or product development.",
  ]},
  { id: 'retention', heading: '4. Data retention', body: [
    "We keep account data as long as your account is active. When you delete your account (Profile → Delete account), we remove your personal data within 30 days, except where we are legally required to keep records (e.g. billing records for tax purposes) or where doing so is necessary to resolve disputes and enforce our agreements.",
    "You can export your data at any time from Profile → Export data.",
  ]},
  { id: 'security', heading: '5. Security practices', body: [
    "Passwords are hashed with bcrypt. Sessions live in httpOnly + Secure cookies with SameSite protection and rotating refresh tokens. Optional two-factor authentication (TOTP) with single-use backup codes is available in Profile → Security. We rate-limit sign-in, sign-up, and password-reset endpoints, and detect brute-force attempts.",
    "No system is perfectly secure. In the unlikely event of a breach affecting your account, we will notify you as required by applicable law.",
  ]},
  { id: 'rights', heading: '6. Your privacy rights', body: [
    "Depending on where you live, you may have rights to:",
    { list: [
      "Access the personal data we hold about you.",
      "Correct inaccurate data.",
      "Delete your data (except where retention is legally required).",
      "Export a copy of your data in a portable format.",
      "Object to certain processing.",
      "Withdraw consent for optional processing.",
    ]},
    "To exercise these rights, use the tools in Profile → Privacy, or email brotherhoodplusapp@gmail.com.",
    "California residents: Brotherhood+ does not sell personal information. You have additional rights under the California Consumer Privacy Act (CCPA) which are honored via the same tools above.",
  ]},
  { id: 'children', heading: '7. Children and age eligibility', body: [
    "Brotherhood+ is intended for adults 18 years or older. We do not knowingly collect personal information from anyone under 18. If you believe a minor has provided information to us, please contact brotherhoodplusapp@gmail.com and we will delete it.",
  ]},
  { id: 'international', heading: '8. International users', body: [
    "Brotherhood+ is operated from the United States. If you access the Service from outside the U.S., you consent to the transfer, processing, and storage of your information in the United States.",
  ]},
  { id: 'changes', heading: '9. Changes to this policy', body: [
    "We may update this Privacy Policy from time to time. When we do, we will update the 'Last updated' date at the top and, for material changes, notify you by email or an in-app notice before the change takes effect.",
  ]},
  { id: 'contact', heading: '10. Contact us', body: [
    "For privacy questions, data requests, or concerns:",
    { list: [
      "Email: brotherhoodplusapp@gmail.com",
    ]},
  ]},
];

function renderBody(item, key) {
  if (typeof item === 'string') return <p key={key} className="mb-3 leading-relaxed text-navy/80">{item}</p>;
  if (item.list) return <ul key={key} className="list-disc list-outside ml-6 mb-3 space-y-1 text-navy/80">{item.list.map((li, i) => <li key={i} className="leading-relaxed">{li}</li>)}</ul>;
  if (item.subheading) return (
    <div key={key} className="mb-3">
      <p className="font-semibold text-navy">{item.subheading}</p>
      <p className="mt-1 leading-relaxed text-navy/80">{item.text}</p>
    </div>
  );
  return null;
}

export default function Privacy() {
  useEffect(() => {
    document.title = 'Privacy Policy · Brotherhood+';
    let m = document.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement('meta'); m.setAttribute('name', 'description'); document.head.appendChild(m); }
    m.setAttribute('content', 'How Brotherhood+ collects, uses, and protects your data. Effective February 12, 2026.');
  }, []);

  return (
    <div className="min-h-screen bg-[#F9F8F6] print:bg-white" data-testid="privacy-page">
      <style>{`@media print { .no-print { display: none !important; } body { background: white; } main { max-width: 720px !important; } }`}</style>
      <div className="max-w-3xl mx-auto p-6 lg:p-10">
        <div className="flex items-center justify-between mb-6 no-print">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-navy/60 hover:text-navy transition-smooth" data-testid="privacy-back">
            <ArrowLeft size={14}/> Home
          </Link>
          <button onClick={() => window.print()} className="inline-flex items-center gap-2 text-xs text-navy/60 hover:text-navy rounded-full border border-navy/15 px-3 py-1.5" aria-label="Print this page" data-testid="privacy-print">
            <Printer size={13}/> Print
          </button>
        </div>

        <main>
          <header className="mb-8">
            <p className="text-[11px] uppercase tracking-[0.24em] text-gold font-semibold">Brotherhood+</p>
            <h1 className="font-heading text-4xl lg:text-5xl mt-2 leading-tight">Privacy Policy</h1>
            <p className="mt-3 text-sm text-navy/55">Effective: {EFFECTIVE_DATE} · Last updated: {LAST_UPDATED}</p>
          </header>

          <nav aria-label="Table of contents" className="mb-8 rounded-2xl bg-white p-5 card-soft no-print">
            <p className="text-[11px] uppercase tracking-widest text-navy/50 font-semibold mb-2">On this page</p>
            <ol className="grid sm:grid-cols-2 gap-1.5 text-sm">
              {SECTIONS.map((s) => (
                <li key={s.id}><a href={`#${s.id}`} className="text-navy hover:text-gold transition-smooth">{s.heading}</a></li>
              ))}
            </ol>
          </nav>

          <div className="space-y-8">
            {SECTIONS.map((s) => (
              <section key={s.id} id={s.id} className="scroll-mt-20" data-testid={`privacy-section-${s.id}`}>
                <h2 className="font-heading text-2xl mb-3">{s.heading}</h2>
                <div>{s.body.map((item, i) => renderBody(item, i))}</div>
              </section>
            ))}
          </div>

          <footer className="mt-12 pt-6 border-t border-navy/10 text-sm text-navy/55">
            <p>Brotherhood+ · Rooted in culture. Made for every man ready to grow.</p>
            <p className="mt-1">Also read: <Link to="/terms" className="underline hover:text-navy">Terms of Service</Link></p>
          </footer>
        </main>
      </div>
    </div>
  );
}
